import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import type { KeyboardEvent, ReactNode } from "react";
import { useRef, useState } from "react";

import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSLogo,
  MDSLogoKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";

export type OnboardingIntent = "meetings" | "quick_thoughts";

interface IntentForkProps {
  backdrop?: ReactNode;
  initialIntent?: OnboardingIntent | null;
  isPending?: boolean;
  onContinue: (intent: OnboardingIntent) => void;
}

interface IntentVignetteProps {
  intent: OnboardingIntent;
  isSelected?: boolean;
}

const INTENTS: readonly OnboardingIntent[] = ["meetings", "quick_thoughts"];

/**
 * Keeps the intent promise visually consistent anywhere onboarding echoes the choice.
 */
export const IntentVignette = ({ intent, isSelected = true }: IntentVignetteProps) => {
  if (intent === "meetings") {
    return (
      <MeetingVignette aria-hidden="true" data-intent-vignette $isSelected={isSelected}>
        <MiniNote>
          <MiniNoteTitle>Team Sync</MiniNoteTitle>
          <MiniNoteRow>
            <CheckboxSkeleton />
            <LineSkeleton $width="76%" />
          </MiniNoteRow>
          <MiniNoteRow>
            <CheckboxSkeleton />
            <LineSkeleton $width="60%" />
          </MiniNoteRow>
        </MiniNote>
        <MeetingAgentLine>
          <AgentAvatar>
            <AgentMark kind={MDSLogoKind.ArrowsCropped} />
          </AgentAvatar>
          <AgentLineText>You said you&apos;d send the deck by today—want help?</AgentLineText>
        </MeetingAgentLine>
      </MeetingVignette>
    );
  }

  return (
    <ThoughtsVignette aria-hidden="true" data-intent-vignette $isSelected={isSelected}>
      <ThoughtCapture>
        <ThoughtCircle>
          <ThoughtVoiceBars aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <span />
          </ThoughtVoiceBars>
        </ThoughtCircle>
        <ThoughtBubble>I need to find a new dentist sometime soon.</ThoughtBubble>
      </ThoughtCapture>
      <ThoughtAgentLine>
        <AgentAvatar>
          <AgentMark kind={MDSLogoKind.ArrowsCropped} />
        </AgentAvatar>
        <AgentLineText>Did you find a dentist yet?</AgentLineText>
      </ThoughtAgentLine>
    </ThoughtsVignette>
  );
};

/**
 * Lets a new user choose which Proactive promise should lead their onboarding.
 *
 * Each vignette intentionally contains at most three visual shapes. The cards
 * demonstrate follow-through rather than accumulating UI details or merely
 * labeling where an input came from.
 */
export const IntentFork = ({
  backdrop,
  initialIntent = null,
  isPending = false,
  onContinue,
}: IntentForkProps) => {
  const [selectedIntent, setSelectedIntent] = useState<OnboardingIntent | null>(initialIntent);
  const meetingCardRef = useRef<HTMLButtonElement>(null);
  const thoughtsCardRef = useRef<HTMLButtonElement>(null);

  const selectIntent = (intent: OnboardingIntent) => {
    setSelectedIntent(intent);
  };

  const focusIntent = (intent: OnboardingIntent) => {
    selectIntent(intent);

    const intentCardRef = intent === "meetings" ? meetingCardRef : thoughtsCardRef;
    intentCardRef.current?.focus();
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const currentIntent = event.currentTarget.dataset.intent as OnboardingIntent;
    const currentIndex = INTENTS.indexOf(currentIntent);
    let nextIntent: OnboardingIntent | undefined;

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      nextIntent = INTENTS[(currentIndex + 1) % INTENTS.length];
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      nextIntent = INTENTS[(currentIndex - 1 + INTENTS.length) % INTENTS.length];
    } else if (event.key === "Home") {
      nextIntent = INTENTS[0];
    } else if (event.key === "End") {
      nextIntent = INTENTS[INTENTS.length - 1];
    }

    if (nextIntent === undefined) {
      return;
    }

    event.preventDefault();
    focusIntent(nextIntent);
  };

  const handleContinue = () => {
    if (selectedIntent === null) {
      return;
    }

    onContinue(selectedIntent);
  };

  return (
    <IntentSurface>
      <Backdrop aria-hidden="true">{backdrop}</Backdrop>
      <Scrim aria-hidden="true" />

      <IntentModal
        aria-busy={isPending}
        aria-labelledby="intent-fork-title"
        aria-modal="true"
        role="dialog"
      >
        <IntentHeader>
          <Title
            id="intent-fork-title"
            as="h1"
            variant={MDSTextVariant.HeaderL}
            weight={MDSTextWeight.SemiBold}
          >
            What should Mem
            <TitleFallbackBreak /> keep track of for you?
          </Title>
          <Subhead variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Secondary}>
            Choose one to start. Mem Agent can help with both.
          </Subhead>
        </IntentHeader>

        <IntentCards role="radiogroup" aria-labelledby="intent-fork-title">
          <IntentCard
            ref={meetingCardRef}
            aria-checked={selectedIntent === "meetings"}
            data-intent="meetings"
            role="radio"
            tabIndex={selectedIntent === null || selectedIntent === "meetings" ? 0 : -1}
            type="button"
            disabled={isPending}
            $isSelected={selectedIntent === "meetings"}
            onClick={() => selectIntent("meetings")}
            onKeyDown={handleCardKeyDown}
          >
            <IntentVignette intent="meetings" isSelected={selectedIntent === "meetings"} />
            <CardCopy>
              <CardTitle>Things from my meetings</CardTitle>
              <CardDescription>
                Find the follow-ups in my meetings and bring them back until they&apos;re handled.
              </CardDescription>
            </CardCopy>
            <CheckIndicator aria-hidden="true" $isSelected={selectedIntent === "meetings"}>
              {selectedIntent === "meetings" ? (
                <MDSIcon kind={MDSIconKind.Check} size={11} />
              ) : null}
            </CheckIndicator>
          </IntentCard>

          <IntentCard
            ref={thoughtsCardRef}
            aria-checked={selectedIntent === "quick_thoughts"}
            data-intent="quick_thoughts"
            role="radio"
            tabIndex={selectedIntent === "quick_thoughts" ? 0 : -1}
            type="button"
            disabled={isPending}
            $isSelected={selectedIntent === "quick_thoughts"}
            onClick={() => selectIntent("quick_thoughts")}
            onKeyDown={handleCardKeyDown}
          >
            <IntentVignette
              intent="quick_thoughts"
              isSelected={selectedIntent === "quick_thoughts"}
            />
            <CardCopy>
              <CardTitle>Things on my mind</CardTitle>
              <CardDescription>
                Tell Mem once—no reminder time needed. It keeps track and follows up later.
              </CardDescription>
            </CardCopy>
            <CheckIndicator aria-hidden="true" $isSelected={selectedIntent === "quick_thoughts"}>
              {selectedIntent === "quick_thoughts" ? (
                <MDSIcon kind={MDSIconKind.Check} size={11} />
              ) : null}
            </CheckIndicator>
          </IntentCard>
        </IntentCards>

        <ContinueButton
          isDisabled={selectedIntent === null || isPending}
          size={MDSButtonSize.Large}
          variant={MDSButtonVariant.Contrast}
          onPress={handleContinue}
        >
          Continue
        </ContinueButton>
      </IntentModal>
    </IntentSurface>
  );
};

const IntentSurface = styled.div`
  position: relative;
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media (max-width: 600px) {
    box-sizing: border-box;
    min-height: 100dvh;
    padding: 12px;
    overflow-y: auto;
  }
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
`;

const scrimIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  background: rgb(28 30 38 / 45%);
  animation: ${scrimIn} 300ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const IntentModal = styled.section`
  position: relative;
  z-index: 1;
  width: 660px;
  max-width: 90vw;
  box-sizing: border-box;
  padding: 36px 36px 30px;
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 20px;
  background: ${({ theme }) => theme.colors.surface.modal};
  box-shadow: ${({ theme }) => theme.shadows.surface};

  @media (max-width: 600px) {
    width: 100%;
    max-width: 440px;
    max-height: calc(100dvh - 24px);
    padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[4]};
    overflow-x: hidden;
    overflow-y: auto;
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  }
`;

const IntentHeader = styled.header`
  text-align: center;
`;

const Title = styled(MDSText)`
  display: block;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.contrast};
  text-wrap: balance;

  @media (max-width: 600px) {
    font-size: 24px;
    line-height: 1.25;
  }
`;

const TitleFallbackBreak = styled.br`
  display: none;

  @supports not (text-wrap: balance) {
    display: initial;
  }
`;

const Subhead = styled(MDSText)`
  display: block;
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const IntentCards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: ${({ theme }) => theme.spacing[7]};

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing[3]};
    margin-top: ${({ theme }) => theme.spacing[5]};
  }
`;

const IntentCard = styled.button<{ $isSelected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 0;
  overflow: hidden;
  padding: 0;
  transform: translateY(0);
  border: 1.5px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.border.emphasis : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  outline: none;
  background: ${({ $isSelected, theme }) => ($isSelected ? "#e6edff" : theme.colors.surface.modal)};
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    transform 180ms ease,
    border-color 180ms ease;

  &:hover,
  &[data-hovered] {
    transform: translateY(-2px);
    border-color: #c4cdea;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const Vignette = styled.div<{ $isSelected: boolean }>`
  position: relative;
  display: block;
  height: 104px;
  flex-shrink: 0;
  overflow: hidden;
  border-bottom: 1px solid #edf0f8;
  background: ${({ theme }) => theme.colors.background.button.chrome.default};
  transition: background 180ms ease;
`;

const MeetingVignette = styled(Vignette)``;

const MiniNote = styled.div`
  position: absolute;
  z-index: 0;
  top: 8px;
  right: 14px;
  bottom: -22px;
  left: 14px;
  padding: 9px 10px;
  border: 1px solid #e3e6f0;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface.modal};

  @media (max-width: 600px) {
    right: auto;
    bottom: -22px;
    left: 50%;
    width: min(280px, calc(100% - 68px));
    box-sizing: border-box;
    transform: translateX(-50%);
  }
`;

const MiniNoteTitle = styled.div`
  margin-bottom: 9px;
  color: #33343c;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
`;

const MiniNoteRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  & + & {
    margin-top: 6px;
  }
`;

const CheckboxSkeleton = styled.span`
  display: block;
  width: 7px;
  height: 7px;
  flex: none;
  border: 1px solid #c7ccda;
  border-radius: 2px;
`;

const LineSkeleton = styled.span<{ $width: string }>`
  display: block;
  width: ${({ $width }) => $width};
  height: 6px;
  border-radius: 4px;
  background: #d9dce5;
`;

const AgentLine = styled.div`
  position: absolute;
  z-index: 1;
  right: auto;
  bottom: 10px;
  left: 14px;
  display: flex;
  width: max-content;
  max-width: calc(100% - 28px);
  box-sizing: border-box;
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  border-radius: ${({ theme }) =>
    `${theme.borderRadius.large} ${theme.borderRadius.large} ${theme.borderRadius.large} 0`};
  background: ${({ theme }) => theme.colors.surface.chat};

  @media (max-width: 600px) {
    bottom: 12px;
  }
`;

const AgentAvatar = styled.span`
  display: flex;
  width: 17px;
  height: 17px;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: #ff5c45;
`;

const AgentMark = styled(MDSLogo)`
  width: 11px;
  height: 7px;

  path {
    fill: white;
  }
`;

const AgentLineText = styled.span`
  min-width: 0;
  color: #33343c;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.3;
`;

const MeetingAgentLine = styled(AgentLine)`
  left: 4px;
  max-width: min(210px, calc(100% - 8px));

  @media (max-width: 600px) {
    right: auto;
    left: max(24px, calc(50% - 150px));
    width: max-content;
    max-width: min(210px, calc(100% - 48px));
    transform: none;
  }
`;

const ThoughtsVignette = styled(Vignette)``;

const ThoughtCapture = styled.div`
  position: absolute;
  top: 16px;
  right: 14px;
  left: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 600px) {
    right: auto;
    left: 50%;
    width: min(320px, calc(100% - 28px));
    transform: translateX(-50%);
  }
`;

const ThoughtCircle = styled.div`
  display: flex;
  width: 34px;
  height: 34px;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: #6792ff;
`;

const ThoughtVoiceBars = styled.span`
  display: flex;
  height: 14px;
  align-items: center;
  gap: 2px;

  > span {
    width: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.max};
    background: white;
  }

  > span:nth-of-type(1) {
    height: 6px;
  }

  > span:nth-of-type(2) {
    height: 12px;
  }

  > span:nth-of-type(3) {
    height: 9px;
  }

  > span:nth-of-type(4) {
    height: 13px;
  }

  > span:nth-of-type(5) {
    height: 7px;
  }
`;

const ThoughtBubble = styled.div`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  padding: 7px 9px;
  border: 1px solid #e3e6f0;
  border-radius: 9px;
  background: white;
  color: #5c6071;
  font-size: 10.5px;
  font-style: italic;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ThoughtAgentLine = styled(AgentLine)`
  bottom: 10px;

  @media (max-width: 600px) {
    right: auto;
    bottom: 10px;
    left: max(14px, calc(50% - 160px));
    width: max-content;
    max-width: min(320px, calc(100% - 28px));
    transform: none;
  }
`;

const CardCopy = styled.div`
  flex-grow: 1;
  padding: 14px 18px;
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const CardTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
`;

const CardDescription = styled.div`
  margin-top: ${({ theme }) => theme.spacing[1]};
  color: #5c6071;
  font-size: 13px;
  line-height: 1.55;
`;

const CheckIndicator = styled.span<{ $isSelected: boolean }>`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ $isSelected }) => ($isSelected ? "#6792ff" : "#d5d9e8")};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: ${({ $isSelected }) => ($isSelected ? "#6792ff" : "white")};
  color: white;

  .mds-icon {
    color: white !important;
  }

  .mds-icon path {
    fill: currentColor !important;
  }
`;

const ContinueButton = styled(MDSButton)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing[6]};
`;
